
import React, { useState } from 'react';
import { Card, CardHeader, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Edit2, Trash2, Power, PowerOff, ChevronRight } from 'lucide-react';
import { format } from 'date-fns';
import { EditCTraderProfileDialog } from './EditCTraderProfileDialog';
import { CTraderLogo } from './ctrader-card/CTraderLogo';
import { useTranslation } from 'react-i18next';
import { useIsMobile } from '@/hooks/use-mobile';
import { useNavigate } from 'react-router-dom';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import type { CTraderCardProps } from './ctrader-card/types';

export function EnhancedCTraderProfileCard({ account, onDisconnect, onReconnect, onDelete }: CTraderCardProps) {
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const navigate = useNavigate();

  const isConnected = account.status === 'connected';
  const platformName = account.broker || account.prop_firm || 'CTrader';

  const handleActionClick = async (action: 'disconnect' | 'reconnect' | 'delete') => {
    setIsLoading(true);
    try {
      switch (action) {
        case 'disconnect':
          await onDisconnect(account.id);
          break;
        case 'reconnect':
          await onReconnect(account.id);
          break;
        case 'delete':
          await onDelete(account.id);
          break;
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleViewDetails = () => {
    navigate(`/ctrader-profiles/${account.id}`);
  };

  return (
    <>
      <Card 
        data-tour="profile-card" 
        className="group overflow-hidden transition-all duration-300 hover:border-primary/40 hover:shadow-lg hover:shadow-primary/10 bg-gradient-to-br from-background to-background/50 border border-border/50"
      >
        <CardHeader className={`border-b border-border/50 bg-gradient-to-r from-muted/30 to-muted/10 ${isMobile ? 'p-3' : 'p-4'}`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 md:gap-3 flex-1 min-w-0">
              <div className="relative flex-shrink-0">
                <CTraderLogo platformName={platformName} />
                {/* Connection status indicator */}
                <div className={`absolute -bottom-1 -right-1 ${isMobile ? 'w-3 h-3' : 'w-4 h-4'} rounded-full border-2 border-background ${
                  isConnected ? 'bg-green-500' : 'bg-orange-500'
                }`}>
                  {isConnected && (
                    <div className="absolute inset-0 bg-green-500 rounded-full animate-ping opacity-75" />
                  )}
                </div>
              </div>
              <div className="flex-1 min-w-0">
                <h3 className={`${isMobile ? 'text-sm' : 'font-semibold'} text-foreground group-hover:text-primary transition-colors truncate`}>
                  {account.csp_account_name}
                </h3>
                <p className={`${isMobile ? 'text-xs' : 'text-sm'} text-muted-foreground truncate`}>
                  {account.csp_account_email}
                </p>
              </div>
            </div>
            
            <Badge 
              variant={isConnected ? "default" : "outline"} 
              className={`${isMobile ? 'text-xs px-2 py-0.5' : 'text-xs font-semibold px-3 py-1'} transition-all duration-200 ${
                isConnected 
                  ? 'bg-green-500/20 text-green-700 hover:bg-green-500/30 border-green-500/30' 
                  : 'bg-orange-500/20 text-orange-700 hover:bg-orange-500/30 border-orange-500/30'
              }`}
            >
              {isConnected ? (
                <div className="flex items-center gap-1">
                  <div className={`${isMobile ? 'h-1.5 w-1.5' : 'h-2 w-2'} bg-green-500 rounded-full animate-pulse`} />
                  <span>{t('connected')}</span>
                </div>
              ) : (
                <div className="flex items-center gap-1">
                  <div className={`${isMobile ? 'h-1.5 w-1.5' : 'h-2 w-2'} bg-orange-500 rounded-full`} />
                  <span>{t('disconnected')}</span>
                </div>
              )}
            </Badge>
          </div>
        </CardHeader>

        <CardContent className={isMobile ? 'p-3' : 'p-4'}>
          {/* Platform Info */}
          <div className={`space-y-${isMobile ? '2' : '3'} ${isMobile ? 'mb-3' : 'mb-4'}`}>
            <div className={`flex justify-between items-center bg-muted/30 rounded-lg ${isMobile ? 'p-2' : 'p-3'}`}>
              <span className={`${isMobile ? 'text-xs' : 'text-sm'} text-muted-foreground font-medium`}>Platform:</span>
              <span className={`${isMobile ? 'text-xs' : 'text-sm'} font-semibold text-foreground truncate max-w-24`}>{platformName}</span>
            </div>
            
            {account.created_at && (
              <div className={`flex justify-between items-center bg-muted/30 rounded-lg ${isMobile ? 'p-2' : 'p-3'}`}>
                <span className={`${isMobile ? 'text-xs' : 'text-sm'} text-muted-foreground font-medium`}>Added:</span>
                <span className={`${isMobile ? 'text-xs' : 'text-sm'} font-semibold text-foreground`}>
                  {format(new Date(account.created_at), isMobile ? 'MMM d, yy' : 'MMM d, yyyy')}
                </span>
              </div>
            )}
          </div>

          {/* Actions */}
          <div data-tour="card-actions" className={`flex items-center justify-between ${isMobile ? 'gap-2' : 'gap-3'}`}>
            <div className={`flex ${isMobile ? 'gap-1' : 'gap-2'}`}>
              <Button
                variant="outline"
                size={isMobile ? "sm" : "default"}
                onClick={() => setIsEditDialogOpen(true)}
                className="hover:bg-primary/10 hover:border-primary/30 transition-all"
                disabled={isLoading}
              >
                <Edit2 className={`${isMobile ? 'h-3 w-3' : 'h-4 w-4'}`} />
                {!isMobile && <span className="ml-2">Edit</span>}
              </Button>

              <Button
                variant="outline"
                size={isMobile ? "sm" : "default"}
                onClick={() => handleActionClick(isConnected ? 'disconnect' : 'reconnect')}
                className={`transition-all ${
                  isConnected 
                    ? 'hover:bg-orange-50 hover:border-orange-300 hover:text-orange-700' 
                    : 'hover:bg-green-50 hover:border-green-300 hover:text-green-700'
                }`}
                disabled={isLoading}
              >
                {isConnected ? (
                  <>
                    <PowerOff className={`${isMobile ? 'h-3 w-3' : 'h-4 w-4'}`} />
                    {!isMobile && <span className="ml-2">Disconnect</span>}
                  </>
                ) : (
                  <>
                    <Power className={`${isMobile ? 'h-3 w-3' : 'h-4 w-4'}`} />
                    {!isMobile && <span className="ml-2">Reconnect</span>}
                  </>
                )}
              </Button>
            </div>

            <div className={`flex ${isMobile ? 'gap-1' : 'gap-2'}`}>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button 
                    variant="ghost" 
                    size="icon"
                    className={`text-destructive hover:bg-destructive/10 hover:text-destructive transition-all ${isMobile ? 'h-8 w-8' : ''}`}
                    disabled={isLoading}
                  >
                    <Trash2 className={`${isMobile ? 'h-3 w-3' : 'h-4 w-4'}`} />
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Delete CTrader Profile</AlertDialogTitle>
                    <AlertDialogDescription>
                      Are you sure you want to delete this CTrader profile? This action cannot be undone and will remove all associated trading accounts.
                      <br />
                      <span className="font-medium text-sm bg-muted px-2 py-1 rounded mt-2 inline-block">
                        {account.csp_account_name}
                      </span>
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction 
                      onClick={() => handleActionClick('delete')}
                      className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                    >
                      Delete Profile
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>

              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button 
                      variant="ghost" 
                      size="icon"
                      onClick={handleViewDetails}
                      className={`opacity-0 group-hover:opacity-100 transition-all duration-200 hover:bg-primary/10 hover:text-primary cursor-pointer ${isMobile ? 'h-8 w-8' : ''}`}
                      disabled={isLoading}
                    >
                      <ChevronRight className={`${isMobile ? 'h-3 w-3' : 'h-4 w-4'}`} />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>View Details</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          </div>
        </CardContent>
      </Card>
      
      <EditCTraderProfileDialog 
        open={isEditDialogOpen} 
        onOpenChange={setIsEditDialogOpen} 
        account={account} 
      />
    </>
  );
}
